import React, { useState, useEffect } from 'react';
import { getAuth, signInWithPopup, signInWithRedirect, GoogleAuthProvider, getRedirectResult } from 'firebase/auth';
import googleLogo from '../assets/google.svg';
import logo from '../assets/logo.svg';

export default function LoginOverlay({ onClose, darkMode = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for redirect result on mount
  useEffect(() => {
    const auth = getAuth();
    getRedirectResult(auth).then((result) => {
      if (result?.user) {
        onClose();
      }
    }).catch((error) => {
      console.error('Redirect sign-in error:', error);
      setError(error.message);
    });
  }, [onClose]);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    
    // Add persistence scope for better cookie handling
    provider.addScope('profile');
    provider.addScope('email');
    
    // Set auth persistence to LOCAL
    try {
      await auth.setPersistence('LOCAL');
    } catch (error) {
      console.warn('Persistence setting failed:', error);
      // Continue anyway as this is not critical
    }
    
    // Check if running on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    try {
      if (isMobile) {
        await signInWithRedirect(auth, provider);
        // Don't close overlay yet - wait for redirect
      } else {
        try {
          const result = await signInWithPopup(auth, provider);
          if (result?.user) {
            onClose();
          }
        } catch (popupError) {
          if (popupError.code === 'auth/popup-blocked') {
            // Fallback to redirect if popup is blocked
            await signInWithRedirect(auth, provider);
            // Don't close overlay yet - wait for redirect
          } else {
            throw popupError;
          }
        }
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${darkMode ? 'bg-gray-900/95' : 'bg-white/95'}`}>
      <div className={`relative w-full max-w-md p-8 rounded-2xl shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* X button */}
        <button
          onClick={onClose}
          className={`absolute top-4 right-4 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Login Content */}
        <div className="text-center">
          <h2 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'} flex items-center justify-center gap-3`}>
            <img src={logo} alt="Logo" className="h-12 w-12" />
            Welcome to {import.meta.env.VITE_APP_TITLE || '.env'}
          </h2>
          <p className={`mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Sign in to start designing your strategy
          </p>

          {/* Google Login Button */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-3 px-6 py-3 text-gray-700 rounded-lg border ${
              darkMode 
                ? 'bg-white hover:bg-gray-100 border-gray-700' 
                : 'bg-white hover:bg-gray-50 border-gray-300'
            } transition-colors duration-200`}
          >
            <img src={googleLogo} alt="Google Logo" className="w-5 h-5" />
            <span className="font-medium">{isLoading ? 'Signing in...' : 'Continue with Google'}</span>
          </button>

          {error && (
            <p className="mt-4 text-red-500 text-sm">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
} 